<template>
  <div class="step">
    <div class="step-header">
      <div class="step-icon">
        <v-icon size="24" color="#e8612f">mdi-map-marker-outline</v-icon>
      </div>
      <div class="step-content">
        <h2 class="title">Business Address</h2>
        <p class="desc">Add your business address details.</p>
      </div>
    </div>

    <v-form class="details-stack">
      <div class="detail-field">
        <AppAddressField
          v-model="line1"
          label="Address Line 1"
          label-icon="mdi-map-marker-outline"
          placeholder="123 Main St"
          variant="outlined"
          prepend-inner-icon=""
          hide-details
          :error-messages="line1Errors"
          @selected="handleAddressSelected"
        />
      </div>

      <div class="detail-field">
        <AppTextField
          v-model="line2"
          label="Address Line 2"
          label-icon="mdi-home-outline"
          placeholder="Suite, Apt, etc."
          :error-messages="line2Errors"
        />
      </div>

      <div class="detail-field">
        <AppTextField
          v-model="city"
          label="City"
          label-icon="mdi-city-variant-outline"
          placeholder="New York"
          :error-messages="cityErrors"
        />
      </div>

      <v-row class="detail-grid">
        <v-col cols="6">
          <div class="detail-field">
            <AppTextField
              v-model="state"
              label="State"
              label-icon="mdi-map-outline"
              placeholder="NY"
              :error-messages="stateErrors"
            />
          </div>
        </v-col>

        <v-col cols="6">
          <div class="detail-field">
            <AppTextField
              v-model="zip"
              label="ZIP"
              label-icon="mdi-mailbox-outline"
              placeholder="10001"
              :error-messages="zipErrors"
            />
          </div>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import AppAddressField from "@/shared/components/ui/AppAddressField.vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";

const props = defineProps<{
  current_step?: number;
  errors?: Record<string, any> | null;
  profileInfo?: any;
}>();

const line1 = ref("");
const line2 = ref("");
const city = ref("");
const state = ref("");
const zip = ref("");
const country = ref("");
const latitude = ref("");
const longitude = ref("");

watch(
  () => props.profileInfo,
  (info) => {
    if (!info) return;

    const address = info?.address || {};
    const line1Value = address?.line_1 || "";
    const line2Value = address?.line_2 || "";
    const cityValue = address?.city || "";
    const stateValue = address?.state || "";
    const zipValue = address?.zip || "";
    const countryValue = address?.country || "";
    const latitudeValue = address?.latitude || address?.lat || "";
    const longitudeValue = address?.longitude || address?.lng || "";

    if (!line1.value && line1Value) line1.value = String(line1Value);
    if (!line2.value && line2Value) line2.value = String(line2Value);
    if (!city.value && cityValue) city.value = String(cityValue);
    if (!state.value && stateValue) state.value = String(stateValue);
    if (!zip.value && zipValue) zip.value = String(zipValue);
    if (!country.value && countryValue) country.value = String(countryValue);
    if (!latitude.value && latitudeValue) latitude.value = String(latitudeValue);
    if (!longitude.value && longitudeValue) longitude.value = String(longitudeValue);
  },
  { immediate: true },
);

function getErrorsForField(field: string): string[] {
  const errs: any = props.errors;
  if (!errs) return [];

  const root = errs?.errors && typeof errs.errors === "object" ? errs.errors : errs;
  const raw = root?.[field];
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((value) => String(value));
  return [String(raw)];
}

const line1Errors = computed(() => getErrorsForField("line_1"));
const line2Errors = computed(() => getErrorsForField("line_2"));
const cityErrors = computed(() => getErrorsForField("city"));
const stateErrors = computed(() => getErrorsForField("state"));
const zipErrors = computed(() => getErrorsForField("zip"));

function handleAddressSelected(payload: Record<string, any> | null) {
  if (!payload || typeof payload !== "object") return;

  const line1Value = String([payload.number, payload.street].filter(Boolean).join(" ")).trim();
  const line2Value = String(payload.unit ?? "").trim();
  const cityValue = String(payload.city ?? "").trim();
  const stateValue = String(payload.state ?? "").trim();
  const zipValue = String(payload.zipcode ?? payload.zip ?? "").trim();
  const countryValue = String(payload.country ?? "").trim();
  const latitudeValue = String(payload.latitude ?? payload.lat ?? "").trim();
  const longitudeValue = String(payload.longitude ?? payload.lng ?? "").trim();

  if (line1Value) line1.value = line1Value;
  if (line2Value) line2.value = line2Value;
  if (cityValue) city.value = cityValue;
  if (stateValue) state.value = stateValue;
  if (zipValue) zip.value = zipValue;
  if (countryValue) country.value = countryValue;
  if (latitudeValue) latitude.value = latitudeValue;
  if (longitudeValue) longitude.value = longitudeValue;
}

function getValues() {
  return {
    line_1: line1.value,
    line_2: line2.value,
    city: city.value,
    state: state.value,
    zip: zip.value,
    country: country.value,
    latitude: latitude.value,
    longitude: longitude.value,
  };
}

defineExpose({ getValues });
</script>

<style scoped>
.step {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 8px;
}

.step-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: var(--radius-lg);
  background: rgba(232, 97, 47, 0.1);
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 4px;
  letter-spacing: -0.025em;
}

.desc {
  margin: 0;
  color: var(--color-subtitle-light);
  font-size: 14px;
  line-height: 1.2;
  font-weight: 400;
}

.details-stack {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.detail-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-grid {
  margin-top: 0;
}
</style>
